import React from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  RocketLaunchIcon,
  BuildingOffice2Icon,
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/solid';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import PublicNavbar from '../common/PublicNavbar';
import PublicFooter from '../common/PublicFooter';
import Footer from '../common/Footer';
import { useCustomerLogin } from './hooks/useCustomerLogin';

const CustomerLogin: React.FC = () => {
  const {
    username, password, showPassword, error, loading,
    companyInfo, subdomainError, mfaPending, mfaCode,
    subdomain, isCompanyPortal,
    setLoginForm, setMfaState,
    handleSubmit, handleMfaSubmit, handleBackToLogin,
    handleResendVerification, unverifiedEmail, resendStatus,
    benefits, lblStyle, inputStyle,
  } = useCustomerLogin();

  /* ── Subdomain portal: company branded login (unchanged) ── */
  if (isCompanyPortal) {
    return (
      <div className="d-flex flex-column min-vh-100">
        {companyInfo && (
          <div style={{
            background: 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
            color: 'white', padding: '10px 20px', textAlign: 'center',
            fontSize: '0.9rem', fontWeight: 500, display: 'flex',
            alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}>
            <BuildingOffice2Icon width={18} height={18} />
            {companyInfo.company_name} Portal
          </div>
        )}

        {subdomainError && (
          <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1" style={{ padding: '2rem' }}>
            <Card style={{ maxWidth: '400px', textAlign: 'center' }}>
              <Card.Body className="p-4">
                <BuildingOffice2Icon width={48} height={48} style={{ color: '#dc2626', marginBottom: '1rem' }} />
                <h4 style={{ color: '#1e293b' }}>Company Not Found</h4>
                <p style={{ color: '#64748b' }}>
                  The company portal <strong>{subdomain}</strong> does not exist.
                </p>
                <a href="https://sourdoughsy.com" className="btn btn-primary">Go to Main Site</a>
              </Card.Body>
            </Card>
          </div>
        )}

        {!subdomainError && (
          <div className="portal-green flex-grow-1 d-flex align-items-center justify-content-center" style={{ padding: '1rem' }}>
            <Container fluid>
              <Row className="w-100">
                <Col xs={12} sm={10} md={8} lg={6} xl={5} className="mx-auto">
                  <Card className="login-card" style={{ maxWidth: '520px', margin: '0 auto' }}>
                    <Card.Body className="p-3 p-sm-4 p-md-5">
                      <div className="text-center mb-4">
                        {companyInfo ? (
                          <>
                            {companyInfo.has_custom_branding && companyInfo.logo_url ? (
                              <div style={{ width: '100px', height: '100px', overflow: 'hidden', margin: '0 auto 0.75rem' }}>
                                <img src={companyInfo.logo_url} alt={`${companyInfo.company_name} Logo`}
                                  style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                              </div>
                            ) : (
                              <div style={{
                                width: '80px', height: '80px', borderRadius: '50%',
                                background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 0.75rem',
                              }}>
                                <BuildingStorefrontIcon width={40} height={40} style={{ color: 'white' }} />
                              </div>
                            )}
                            <h2 className="fw-bold" style={{ color: '#065f46', fontSize: '1.5rem' }}>{companyInfo.company_name}</h2>
                            <p style={{ color: '#64748b', fontSize: '0.95rem' }}>Customer Portal</p>
                            {!companyInfo.has_custom_branding && (
                              <p style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '-4px' }}>Powered by Sourdoughsy</p>
                            )}
                          </>
                        ) : (
                          <>
                            <div style={{
                              width: '80px', height: '80px', borderRadius: '50%',
                              background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              margin: '0 auto 0.75rem',
                            }}>
                              <BuildingStorefrontIcon width={40} height={40} style={{ color: 'white' }} />
                            </div>
                            <h2 className="fw-bold" style={{ color: '#065f46', fontSize: '1.5rem' }}>Customer Portal</h2>
                          </>
                        )}
                      </div>

                      {error && (
                        <Alert variant={unverifiedEmail ? 'warning' : 'danger'} className="py-2 px-3" style={{ fontSize: '0.9rem' }}>
                          {error}
                          {unverifiedEmail && (
                            <div className="mt-2">
                              <button
                                type="button"
                                onClick={handleResendVerification}
                                disabled={resendStatus !== 'idle'}
                                style={{
                                  background: 'none', border: 'none', padding: 0,
                                  color: '#065f46', fontWeight: 600, textDecoration: 'underline',
                                  cursor: resendStatus !== 'idle' ? 'default' : 'pointer',
                                  fontSize: '0.85rem',
                                }}
                              >
                                {resendStatus === 'sent' ? '✓ Verification email sent!' : resendStatus === 'sending' ? 'Sending...' : 'Resend verification email'}
                              </button>
                            </div>
                          )}
                        </Alert>
                      )}

                      {mfaPending ? (
                        <Form onSubmit={handleMfaSubmit}>
                          <div className="text-center mb-3">
                            <ShieldCheckIcon width={36} height={36} style={{ color: '#065f46', marginBottom: '8px' }} />
                            <p style={{ color: '#374151', fontWeight: 600, fontSize: '0.95rem', marginBottom: '4px' }}>Two-Factor Authentication</p>
                            <p style={{ color: '#64748b', fontSize: '0.85rem' }}>Enter the 6-digit code from your authenticator app</p>
                          </div>
                          <Form.Group className="mb-4">
                            <Form.Control type="text" placeholder="000000" inputMode="numeric" pattern="[0-9]*"
                              value={mfaCode} onChange={(e) => setMfaState(prev => ({ ...prev, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                              required autoComplete="one-time-code" maxLength={6}
                              style={{ ...inputStyle, textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em', fontWeight: 700 }} />
                          </Form.Group>
                          <Button type="submit" disabled={loading || mfaCode.length !== 6}
                            className="w-100 mb-3 d-flex align-items-center justify-content-center gap-2"
                            style={{
                              backgroundColor: '#065f46', borderColor: '#065f46', color: 'white',
                              padding: '14px', fontSize: '1rem', fontWeight: '600',
                              borderRadius: '10px', minHeight: '48px',
                            }}>
                            <ShieldCheckIcon width={20} height={20} />
                            {loading ? 'Verifying...' : 'Verify Code'}
                          </Button>
                          <Button variant="link" className="w-100" style={{ color: '#64748b', fontSize: '0.85rem' }}
                            onClick={handleBackToLogin}>
                            &larr; Back to login
                          </Button>
                        </Form>
                      ) : (
                        <Form onSubmit={handleSubmit}>
                          <Form.Group className="mb-3">
                            <Form.Label style={lblStyle}>Username or Customer ID</Form.Label>
                            <Form.Control type="text" placeholder="Enter your username or customer ID"
                              value={username} onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
                              required autoComplete="username" autoCapitalize="none" style={inputStyle} />
                          </Form.Group>
                          <Form.Group className="mb-4">
                            <Form.Label style={lblStyle}>Password</Form.Label>
                            <div style={{ position: 'relative' }}>
                              <Form.Control type={showPassword ? 'text' : 'password'} placeholder="Enter your password"
                                value={password} onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                                required autoComplete="current-password" style={{ ...inputStyle, paddingRight: '48px' }} />
                              <button type="button" tabIndex={-1} onClick={() => setLoginForm(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                                style={{ position: 'absolute', right: '1px', top: '1px', bottom: '1px', width: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '0 9px 9px 0' }}>
                                {showPassword ? <EyeSlashIcon style={{ width: '18px', height: '18px', color: '#64748b' }} /> : <EyeIcon style={{ width: '18px', height: '18px', color: '#64748b' }} />}
                              </button>
                            </div>
                          </Form.Group>
                          <Button type="submit" disabled={loading}
                            className="w-100 mb-3 d-flex align-items-center justify-content-center gap-2"
                            style={{
                              backgroundColor: '#065f46', borderColor: '#065f46', color: 'white',
                              padding: '14px', fontSize: '1rem', fontWeight: '600',
                              borderRadius: '10px', minHeight: '48px',
                            }}>
                            <RocketLaunchIcon width={20} height={20} />
                            {loading ? 'Logging in...' : 'Access Dashboard'}
                          </Button>
                          <div className="text-end mb-3">
                            <Link to="/forgot-password" style={{ color: '#065f46', fontSize: '0.875rem', textDecoration: 'none' }}>
                              Forgot password?
                            </Link>
                          </div>
                        </Form>
                      )}
                      <div className="text-center">
                        <p className="text-muted small mb-0">
                          {companyInfo ? `Welcome to ${companyInfo.company_name}'s portal` : 'Welcome to your wholesale portal'}
                        </p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Container>
          </div>
        )}
        <Footer />
      </div>
    );
  }

  /* ── Main domain: two-column login (matching Register page style) ── */
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicNavbar solid />

      <div style={{ flex: 1, paddingTop: '72px' }}>
        <Row className="g-0 m-0" style={{ minHeight: 'calc(100vh - 72px)' }} id="login-row">

          {/* Left panel – gradient hero (desktop only) */}
          <Col lg={5} className="d-none d-lg-flex"
            style={{
              background: 'linear-gradient(160deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)',
              display: 'flex', flexDirection: 'column', justifyContent: 'center',
              padding: 'clamp(40px, 5vw, 80px)', position: 'relative', overflow: 'hidden',
            }}>
            <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '260px', height: '260px', borderRadius: '50%', background: 'rgba(59,130,246,0.12)' }} />
            <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(59,130,246,0.08)' }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div className="d-flex align-items-center gap-2 mb-4">
                <BuildingStorefrontIcon style={{ width: '28px', height: '28px', color: '#60a5fa' }} />
                <span style={{ fontSize: '1.15rem', fontWeight: 700, color: 'white' }}>Sourdoughsy</span>
              </div>

              <h2 style={{ color: 'white', fontWeight: 800, fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', lineHeight: 1.3, marginBottom: '0.75rem' }}>
                Welcome back to your wholesale bakery hub
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '1rem', lineHeight: 1.6, marginBottom: '2.5rem' }}>
                Sign in to manage your customers, orders, and finances — all in one place.
              </p>

              <div className="d-flex flex-column gap-3">
                {benefits.map((b) => (
                  <div key={b.title} className="d-flex align-items-start gap-3">
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                      background: 'rgba(255,255,255,0.08)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <b.icon style={{ width: '20px', height: '20px', color: '#60a5fa' }} />
                    </div>
                    <div>
                      <div style={{ color: 'white', fontWeight: 700, fontSize: '0.92rem', marginBottom: '1px' }}>{b.title}</div>
                      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.83rem', lineHeight: 1.45 }}>{b.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Col>

          {/* Right – Login Form */}
          <Col lg={7} xs={12}
            className="login-form-col"
            style={{
              background: '#f8fafc', display: 'flex', alignItems: 'center',
              justifyContent: 'center', padding: 'clamp(24px, 4vw, 48px)',
            }}>
            <div style={{ maxWidth: '440px', width: '100%' }}>
              {/* Mobile-only branding */}
              <div className="d-lg-none text-center mb-4">
                <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                  <BuildingStorefrontIcon style={{ width: '24px', height: '24px', color: '#1e3a8a' }} />
                  <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e3a8a' }}>Sourdoughsy</span>
                </div>
              </div>

              <h2 style={{ fontWeight: 800, color: '#0f172a', marginBottom: '4px', fontSize: 'clamp(1.4rem, 3vw, 1.7rem)' }}>
                Sign in to your account
              </h2>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '1.75rem' }}>
                Access your bakery dashboard
              </p>

              {error && (
                <Alert variant={unverifiedEmail ? 'warning' : 'danger'} className="py-2 px-3" style={{ fontSize: '0.88rem', borderRadius: '10px' }}>
                  {error}
                  {unverifiedEmail && (
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={resendStatus !== 'idle'}
                        style={{
                          background: 'none', border: 'none', padding: 0,
                          color: '#1e3a8a', fontWeight: 600, textDecoration: 'underline',
                          cursor: resendStatus !== 'idle' ? 'default' : 'pointer',
                          fontSize: '0.85rem',
                        }}
                      >
                        {resendStatus === 'sent' ? '✓ Verification email sent!' : resendStatus === 'sending' ? 'Sending...' : 'Resend verification email'}
                      </button>
                    </div>
                  )}
                </Alert>
              )}

              {mfaPending ? (
                <Form onSubmit={handleMfaSubmit}>
                  <div className="text-center mb-3">
                    <ShieldCheckIcon style={{ width: '36px', height: '36px', color: '#1e3a8a', marginBottom: '8px' }} />
                    <p style={{ color: '#0f172a', fontWeight: 700, fontSize: '1rem', marginBottom: '4px' }}>Two-Factor Authentication</p>
                    <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Enter the 6-digit code from your authenticator app</p>
                  </div>
                  <Form.Group className="mb-4">
                    <Form.Control type="text" placeholder="000000" inputMode="numeric" pattern="[0-9]*"
                      value={mfaCode} onChange={(e) => setMfaState(prev => ({ ...prev, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                      required autoComplete="one-time-code" maxLength={6}
                      style={{ ...inputStyle, textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em', fontWeight: 700 }} />
                  </Form.Group>
                  <Button type="submit" disabled={loading || mfaCode.length !== 6}
                    className="w-100 d-flex align-items-center justify-content-center gap-2"
                    style={{
                      borderRadius: '12px', padding: '14px', fontWeight: 700, fontSize: '1rem',
                      background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', color: 'white',
                      border: 'none', boxShadow: '0 4px 15px rgba(30,58,138,0.25)', minHeight: '50px',
                    }}>
                    <ShieldCheckIcon style={{ width: '20px', height: '20px' }} />
                    {loading ? 'Verifying...' : 'Verify Code'}
                  </Button>
                  <Button variant="link" className="w-100 mt-2" style={{ color: '#94a3b8', fontSize: '0.85rem' }}
                    onClick={handleBackToLogin}>
                    &larr; Back to login
                  </Button>
                </Form>
              ) : (
                <>
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label style={lblStyle}>Username or Customer ID</Form.Label>
                      <Form.Control type="text" placeholder="Enter your username or customer ID"
                        value={username} onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
                        required autoComplete="username" autoCapitalize="none" style={inputStyle} />
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label style={lblStyle}>Password</Form.Label>
                      <div style={{ position: 'relative' }}>
                        <Form.Control type={showPassword ? 'text' : 'password'} placeholder="Enter your password"
                          value={password} onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                          required autoComplete="current-password" style={{ ...inputStyle, paddingRight: '48px' }} />
                        <button type="button" tabIndex={-1} onClick={() => setLoginForm(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                          style={{ position: 'absolute', right: '1px', top: '1px', bottom: '1px', width: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '0 9px 9px 0' }}>
                          {showPassword ? <EyeSlashIcon style={{ width: '18px', height: '18px', color: '#64748b' }} /> : <EyeIcon style={{ width: '18px', height: '18px', color: '#64748b' }} />}
                        </button>
                      </div>
                    </Form.Group>

                    <Button type="submit" disabled={loading}
                      className="w-100 d-flex align-items-center justify-content-center gap-2"
                      style={{
                        borderRadius: '12px', padding: '14px', fontWeight: 700, fontSize: '1rem',
                        background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', color: 'white',
                        border: 'none', boxShadow: '0 4px 15px rgba(30,58,138,0.25)', minHeight: '50px',
                      }}>
                      <RocketLaunchIcon style={{ width: '20px', height: '20px' }} />
                      {loading ? 'Signing in...' : 'Sign In'}
                    </Button>
                    <div className="text-end mt-2">
                      <Link to="/forgot-password" style={{ color: '#1e3a8a', fontSize: '0.85rem', textDecoration: 'none' }}>
                        Forgot password?
                      </Link>
                    </div>
                  </Form>

                  <p className="text-center mt-3" style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: 0 }}>
                    Don't have an account?{' '}
                    <Link to="/register" style={{ color: '#1e3a8a', fontWeight: 600, textDecoration: 'none' }}>
                      Create one for free
                    </Link>
                  </p>
                </>
              )}
            </div>
          </Col>
        </Row>
      </div>

      <PublicFooter />
    </div>
  );
};

export default CustomerLogin;